import { api } from './client';
import type { DriveDocuments } from './drive';

export type KlientStatus = 'active' | 'paused' | 'ended';

export interface KlientListItem {
  id: number;
  name: string;
  company: string | null;
  logo: string | null;
  logoBg: string | null;
  website: string | null;
  market: string | null;
  product: string | null;
  module: string | null;
  monthlyFeeEur: number | null;
  clientSince: string | null;
  status: KlientStatus;
  contactsCount: number;
  leadId: number | null;
}

export interface KlientDetail extends KlientListItem {
  ic: string | null;
  vat: string | null;
  focus: string | null;
  platform: string | null;
  affiliatePlatform: string | null;
  contractFrom: string | null;
  contractTo: string | null;
  note: string | null;
  notesCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface KlientInput {
  name: string;
  company?: string | null;
  ic?: string | null;
  vat?: string | null;
  website?: string | null;
  market?: string | null;
  focus?: string | null;
  product?: string | null;
  module?: string | null;
  platform?: string | null;
  affiliatePlatform?: string | null;
  monthlyFeeEur?: number | null;
  contractFrom?: string | null;
  contractTo?: string | null;
  clientSince?: string | null;
  status?: KlientStatus;
  note?: string | null;
}

export interface KlientContact {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  vocative: string | null;
}

export interface KlientContactInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  position?: string | null;
  vocative?: string | null;
}

export interface KlientNote {
  id: number;
  body: string;
  source: string;
  notedAt: string | null;
  createdBy: string | null;
}

export interface KlientReporting {
  ready: boolean;
  reason: string | null;
  months: unknown[];
  affiliates: unknown[];
  invoices: unknown[];
}

const BASE = '/api/v1/klienti';

export function listKlienti(): Promise<KlientListItem[]> {
  return api.get(BASE);
}
export function getKlient(id: number): Promise<KlientDetail> {
  return api.get(`${BASE}/${id}`);
}
export function getKlientReporting(id: number): Promise<KlientReporting> {
  return api.get(`${BASE}/${id}/reporting`);
}
export function createKlient(input: KlientInput): Promise<{ id: number }> {
  return api.post(BASE, input);
}
export function updateKlient(id: number, patch: Partial<KlientInput>): Promise<void> {
  return api.post(`${BASE}/${id}/update`, patch);
}
export function deleteKlient(id: number): Promise<void> {
  return api.post(`${BASE}/${id}/delete`, {});
}

export function getKlientContacts(id: number): Promise<KlientContact[]> {
  return api.get(`${BASE}/${id}/contacts`);
}
export function addKlientContact(id: number, input: KlientContactInput): Promise<{ id: number }> {
  return api.post(`${BASE}/${id}/contacts`, input);
}
export function updateKlientContact(id: number, contactId: number, input: KlientContactInput): Promise<void> {
  return api.post(`${BASE}/${id}/contacts/${contactId}/update`, input);
}
export function deleteKlientContact(id: number, contactId: number): Promise<void> {
  return api.post(`${BASE}/${id}/contacts/${contactId}/deactivate`, {});
}

export function getKlientNotes(id: number): Promise<KlientNote[]> {
  return api.get(`${BASE}/${id}/notes`);
}
export function addKlientNote(id: number, body: string): Promise<{ id: number }> {
  return api.post(`${BASE}/${id}/notes`, { body });
}
export function updateKlientNote(id: number, noteId: number, body: string): Promise<void> {
  return api.post(`${BASE}/${id}/notes/${noteId}/update`, { body });
}
export function deleteKlientNote(id: number, noteId: number): Promise<void> {
  return api.post(`${BASE}/${id}/notes/${noteId}/delete`, {});
}

export type KlientDealKind = 'fix' | 'rs_fee' | 'cpa_fee' | 'reg_fee' | 'affiliate_passthrough';
export type KlientDealInvoiceGroup = 'fixni' | 'provize';
export type KlientDealStatus = 'pripraveno' | 'aktivni' | 'ukonceny';
export interface KlientDeal {
  id: number; kind: KlientDealKind; title: string; amount: number | null; percent: number | null;
  currency: string; exchangeRate: number | null; invoiceGroup: KlientDealInvoiceGroup; uolProductId: string | null;
  validFrom: string | null; validTo: string | null; active: boolean; status: KlientDealStatus; note: string | null;
  excludedAffiliates: string[];
  includeDeclined: boolean;
}
export interface KlientDealInput {
  kind: KlientDealKind; title: string; amount?: string | number | null; percent?: string | number | null;
  currency: 'CZK' | 'EUR'; exchangeRate?: string | number | null; invoiceGroup: KlientDealInvoiceGroup;
  uolProductId?: string | null; validFrom?: string | null; validTo?: string | null; status?: KlientDealStatus; note?: string | null;
  excludedAffiliates?: string[];
  includeDeclined?: boolean;
}

export function getKlientDeals(id: number): Promise<KlientDeal[]> {
  return api.get(`${BASE}/${id}/deals`);
}
export function addKlientDeal(id: number, input: KlientDealInput): Promise<{ id: number }> {
  return api.post(`${BASE}/${id}/deals`, input);
}
export function updateKlientDeal(id: number, dealId: number, input: KlientDealInput): Promise<void> {
  return api.post(`${BASE}/${id}/deals/${dealId}/update`, input);
}
export function setKlientDealStatus(id: number, dealId: number, status: KlientDealStatus): Promise<void> {
  return api.post(`${BASE}/${id}/deals/${dealId}/status`, { status });
}

export function uploadKlientLogo(id: number, contentBase64: string, mime: string, logoBg: string | null): Promise<{ logo: string; logoBg: string | null }> {
  return api.post(`${BASE}/${id}/logo`, { contentBase64, mime, logoBg });
}

export function getKlientDocuments(id: number): Promise<DriveDocuments> {
  return api.get(`${BASE}/${id}/documents`);
}
export function refreshKlientDocuments(id: number): Promise<DriveDocuments> {
  return api.post(`${BASE}/${id}/documents/refresh`, {});
}
export function setKlientDriveFolder(id: number, url: string | null): Promise<void> {
  return api.post(`${BASE}/${id}/drive-folder`, { url: url ?? '' });
}

export interface KlientBilling {
  ic: string | null;
  vat: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  billingEmails: string[];
  billingCurrency: 'CZK' | 'EUR';
  uolContactId: string | null;
  uolCompanyName: string | null;
}

export interface KlientBillingInput {
  street?: string | null;
  city?: string | null;
  zip?: string | null;
  billingEmails?: string[];
  billingCurrency?: 'CZK' | 'EUR';
  uolContactId?: string | null;
  uolCompanyName?: string | null;
}

export function getKlientBilling(id: number): Promise<KlientBilling> {
  return api.get(`${BASE}/${id}/billing`);
}
export function updateKlientBilling(id: number, input: KlientBillingInput): Promise<void> {
  return api.post(`${BASE}/${id}/billing`, input);
}
export function lookupKlientUol(id: number): Promise<{ found: boolean; contactId?: string; name?: string | null }> {
  return api.post(`${BASE}/${id}/uol-lookup`, {});
}
